import React, { Component } from 'react';

const SWATCH_SIZE = 24;
const SWATCH_GAP = 5;

const CMS_URL = process.env.REACT_APP_API_URL || '';

/**
 * AthleticColorChart
 *
 * Fetches the athletic color palette from the Payload CMS colors API and
 * renders it as tonal columns (hue families) with a greyscale row at the bottom.
 *
 * Props:
 *   selectedColor  – currently active hex (string)
 *   onSelect       – (hex) => void
 */
class AthleticColorChart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            columns: [],    // [{ label, swatches: [{ hex, name } | null] }]
            greys: [],      // [{ hex, name }]
            loading: true,
        };
    }

    componentDidMount() {
        fetch(`${CMS_URL}/api/colors?limit=500&sort=groupOrder,order`)
            .then((r) => r.json())
            .then((json) => {
                const docs = json.docs || [];
                this._buildLayout(docs);
            })
            .catch((err) => {
                console.error('AthleticColorChart: failed to load colors', err);
                this.setState({ loading: false });
            });
    }

    _buildLayout(docs) {
        // Separate greyscale (groupOrder === 99) from chromatic columns
        const chromatic = docs.filter((d) => d.groupOrder !== 99);
        const greyDocs  = docs.filter((d) => d.groupOrder === 99);

        // Group chromatic docs into columns by groupOrder
        const colMap = {};
        chromatic.forEach((doc) => {
            const gOrder = doc.groupOrder;
            if (!colMap[gOrder]) {
                colMap[gOrder] = { label: doc.group, swatches: [] };
            }
            colMap[gOrder].swatches.push(doc);
        });

        // Sort columns by groupOrder, sort swatches within each column by order
        const columns = Object.keys(colMap)
            .map(Number)
            .sort((a, b) => a - b)
            .map((gOrder) => {
                const col = colMap[gOrder];
                const sortedSwatches = col.swatches.sort((a, b) => a.order - b.order);

                // Find the max row index to know grid height, fill gaps with null
                const maxOrder = sortedSwatches.reduce((m, s) => Math.max(m, s.order), 0);
                const grid = Array.from({ length: maxOrder + 1 }, (_, i) => {
                    const match = sortedSwatches.find((s) => s.order === i);
                    return match ? { hex: match.hex, name: match.name } : null;
                });

                return { label: col.label, swatches: grid };
            });

        const greys = greyDocs
            .sort((a, b) => a.order - b.order)
            .map((d) => ({ hex: d.hex, name: d.name }));

        this.setState({ columns, greys, loading: false });
    }

    render() {
        const { selectedColor, onSelect } = this.props;
        const { columns, greys, loading } = this.state;

        const swatchStyle = (hex, isWhite) => ({
            width: SWATCH_SIZE,
            height: SWATCH_SIZE,
            borderRadius: '50%',
            backgroundColor: hex,
            border: selectedColor === hex
                ? '2px solid #ecff19'
                : isWhite ? '1px solid #444' : '2px solid transparent',
            outline: selectedColor === hex ? '1px solid #555' : 'none',
            boxSizing: 'border-box',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'border-color 0.12s',
        });

        const emptyStyle = {
            width: SWATCH_SIZE,
            height: SWATCH_SIZE,
            flexShrink: 0,
        };

        if (loading) {
            return (
                <div style={{ fontSize: '11px', color: '#555', fontStyle: 'italic' }}>
                    Loading colors…
                </div>
            );
        }

        return (
            <div>
                {/* ── Colour columns ── */}
                <div style={{ display: 'flex', gap: SWATCH_GAP, marginBottom: SWATCH_GAP + 4 }}>
                    {columns.map((col, ci) => (
                        <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: SWATCH_GAP }}>
                            {col.swatches.map((s, ri) =>
                                s ? (
                                    <div
                                        key={ri}
                                        style={swatchStyle(s.hex, false)}
                                        title={s.name}
                                        onClick={() => onSelect(s.hex)}
                                    />
                                ) : (
                                    <div key={ri} style={emptyStyle} />
                                )
                            )}
                        </div>
                    ))}
                </div>

                {/* ── Divider ── */}
                <div style={{ borderTop: '1px solid #2a2a2a', margin: '6px 0' }} />

                {/* ── Greyscale row ── */}
                <div style={{ display: 'flex', gap: SWATCH_GAP, flexWrap: 'wrap' }}>
                    {greys.map((s) => (
                        <div
                            key={s.hex}
                            style={swatchStyle(s.hex, s.hex === '#ffffff')}
                            title={s.name}
                            onClick={() => onSelect(s.hex)}
                        />
                    ))}
                    {/* Custom picker */}
                    <input
                        type="color"
                        value={selectedColor || '#ffffff'}
                        title="Custom colour"
                        onChange={(e) => onSelect(e.target.value)}
                        style={{
                            width: SWATCH_SIZE,
                            height: SWATCH_SIZE,
                            borderRadius: '50%',
                            border: '1px solid #444',
                            padding: 0,
                            cursor: 'pointer',
                            background: 'none',
                            boxSizing: 'border-box',
                            flexShrink: 0,
                        }}
                    />
                </div>
            </div>
        );
    }
}

export default AthleticColorChart;
